import { Trans } from 'react-i18next'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import { v4 as uuid4 } from 'uuid'

import { BuilderMenu, VariableSelectFeatureFlag } from '@makyoapp/makyo-common'
import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, MATERIAL_ICONS } from '@makyoapp/makyo-ui'

import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useSortableHandle } from '@/hooks/useSortableHandle'
import { genIdent } from '@/shared/utils'
import { AccordionItem } from '@/components/Accordion/Accordion'

import SortableMenuItem from './SortableMenuItem'

/**
 * MenuItemAccordion is a function component that provides an interface for listing and editing menu items in a builder.
 * It also uses the useSortableHandle hook to provide a way to sort the menu items via drag and drop.
 */
function MenuItemAccordion({ menu, menuIdx }: { menuIdx: number; menu: BuilderMenu }) {
  const { setMenus, updateMenu } = useAppBuilderContext()

  const { sensors, handleDragEnd } = useSortableHandle((active, over) => {
    setMenus((prev) => {
      const copy = [...prev]
      const items = copy[menuIdx].items
      const oldIndex = items.findIndex((p) => p.id === active.id)
      const newIndex = items.findIndex((p) => p.id === over.id)
      copy[menuIdx].items = arrayMove(items, oldIndex, newIndex)
      return copy
    })
  })

  // The menu items listing and editing interface
  return (
    <AccordionItem itemId='menu-items' title={<Trans>Menu Items</Trans>}>
      <div className='flex flex-col pb-4'>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToFirstScrollableAncestor]}
        >
          <SortableContext items={menu.items}>
            {menu.items.map((item) => {
              return <SortableMenuItem menuIdx={menuIdx} menuItem={item} key={item.id} />
            })}
          </SortableContext>
        </DndContext>
      </div>
      <Button
        icon={<Plus16 />}
        onClick={async () => {
          await updateMenu(menuIdx, (prev) => {
            const prevItems = prev.items
            prev.items.push({
              id: uuid4(),
              title: genIdent(
                prevItems as unknown as Record<string, unknown>[],
                'title',
                prevItems.length + 1,
                'Menu Item '
              ),
              icon: {
                id: uuid4(),
                flag: VariableSelectFeatureFlag.Icon,
                value: MATERIAL_ICONS[Math.floor(Math.random() * MATERIAL_ICONS.length)]?.value ?? 'home',
              },
              //actions: [],
              action: undefined,
              badgeSize: 's',
              badgeMetadata: undefined,
            })
          })
        }}
        className='w-full'
      >
        <Trans>New Menu Item</Trans>
      </Button>
    </AccordionItem>
  )
}

export default MenuItemAccordion
